import {
  CheckCheck,
  ChevronRight,
  GalleryVertical,
  GalleryVerticalEnd,
  Layers,
  LayoutGrid,
  Rows3,
  Settings,
} from 'lucide-react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router'
import { MomentsIcon, PhotosIcon, RecipesIcon } from '@/components/nav/icons'
import { ActionMenu } from '@/components/ActionMenu'
import { FilterButton, type Filter } from '@/components/Filters'
import { FormError } from '@/components/FormError'
import { HomeAdd } from '@/components/home/HomeAdd'
import { page } from '@/components/layout'
import { Initial } from '@/components/paper'
import { PersonProfile } from '@/components/people/PersonProfile'
import { useOpenPerson } from '@/lib/connections'
import { albumPhotoUrl, photoSrcSet } from '@/lib/albums'
import type { Me } from '@/lib/api'
import type { Family } from '@/lib/families'
import { dayLabel, localDay, onThisDayShown, useOnThisDay, type Who } from '@/lib/home'
import {
  feedRatio,
  KIND_LABELS,
  kindOf,
  kindsIn,
  narrowed,
  newPeople,
  photoDots,
  photosLine,
  readArrange,
  readCaughtUp,
  rememberArrange,
  rememberCaughtUp,
  sinceLabel,
  useHomeNew,
  useSeeHome,
  useWander,
  type Arrange,
  type Kind,
  type MomentItem,
  type Narrow,
  type NewItem,
  type PhotosItem,
  type RecipeItem,
  type Shelf,
  type ShelfItem,
  type ShelfPhoto,
} from '@/lib/homeBlend'
import { momentPhotoUrl } from '@/lib/moments'
import { useMyPeople } from '@/lib/myPeople'
import { tint } from '@/lib/paper'
import { fromMyPeople, type ShownPerson } from '@/lib/peopleList'
import { photoUrl } from '@/lib/photos'
import { useFrame } from '@/lib/frame'
import { mySections } from '@/lib/sections'
import { useCurrentSpace } from '@/lib/spaces'
import { firstName } from '@/lib/tree'
import { cn } from '@/lib/utils'
import { usePhotoLink } from '@/lib/photoMotion'

const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
const SECTION_PATHS: Record<Kind, string> = { photos: 'photos', moments: 'moments', recipes: 'recipes' }

// Home in the Modern style: no welcome; your picture's ring says which circle you're in (YouFace). Along
// the top, rings for On this day and for each person with something new; then everything new since you last looked,
// one after another as a feed, blended on one wall or shelved by section, ending "You're caught up"; then Wander, a
// few shelves to explore. No counts, no ranking: new things come newest first and the shelves are the day's pick.
export function ModernHome({ family, me }: { family: Family; me: Me }) {
  const sections = mySections(me, family)
  const { actionSlot } = useFrame()
  const space = useCurrentSpace(family)
  const [caughtUp, setCaughtUp] = useState(readCaughtUp)
  const fresh = useHomeNew(family.id, space?.id, !caughtUp)
  useSeeHome(family.id, fresh.isSuccess)
  const [narrow, setNarrow] = useState<Narrow>(null)
  const [arrange, setArrange] = useState<Arrange>(readArrange)
  const items = fresh.data ? narrowed(fresh.data.items, null, sections) : []
  // The rings are for people with something new, whether or not the feed reaches back further.
  const people = newPeople(items.filter((item) => item.at > (fresh.data?.since ?? 0)))
  const shown = narrowed(items, narrow, sections)
  const kinds = kindsIn(items)
  const since = fresh.data ? sinceLabel(fresh.data.since) : ''
  // By section needs two kinds of thing; with fewer it's the tiles.
  const view: Arrange = arrange === 'sections' && kinds.length < 2 ? 'mixed' : arrange
  const views: Arrange[] = kinds.length > 1 ? ['feed', 'mixed', 'sections'] : ['feed', 'mixed']
  const pick = (next: Arrange) => {
    setArrange(next)
    rememberArrange(next)
  }
  const filters: Filter[] = []
  if (kinds.length > 1)
    filters.push({
      heading: 'Show',
      label: 'Narrow what’s new',
      value: narrow && 'kind' in narrow ? narrow.kind : 'all',
      usual: 'all',
      icons: { all: Layers, photos: PhotosIcon, moments: MomentsIcon, recipes: RecipesIcon },
      options: [['all', 'All'], ...kinds.map((kind): [string, string] => [kind, KIND_LABELS[kind]])],
      onChange: (kind) => setNarrow(kind === 'all' ? null : { kind: kind as Kind }),
    })
  filters.push({
    heading: 'Caught up',
    label: 'Only what’s new since your last visit',
    value: caughtUp ? 'on' : 'off',
    usual: 'off',
    icons: { off: GalleryVertical, on: CheckCheck },
    options: [
      ['off', 'Off'],
      ['on', 'On'],
    ],
    onChange: (next) => {
      setCaughtUp(next === 'on')
      rememberCaughtUp(next === 'on')
    },
  })
  const toggle = (next: Narrow) => setNarrow((was) => (JSON.stringify(was) === JSON.stringify(next) ? null : next))
  // The view, then Filter, beside Home's "+" at the top right: in the phone's top bar, beside the logo.
  const controls = (
    <>
      {items.length > 0 && <HomeViews views={views} value={view} onChange={(next) => pick(next as Arrange)} />}
      <FilterButton
        title="Filter"
        iconOnly
        className="bg-transparent max-nav:w-9 max-nav:hover:bg-transparent max-nav:active:opacity-60 nav:size-10 nav:hover:bg-secondary [&>svg]:size-5"
        filters={filters}
        footer={
          !family.everyone && (
            <Link
              to={`/circle/${family.id}/settings`}
              className={cn(
                '-mx-1 flex min-h-11 items-center gap-3 rounded-xl px-1 font-medium hover:bg-secondary',
                link,
              )}
            >
              <Settings className="size-5 text-muted-foreground" aria-hidden />
              Circle settings
            </Link>
          )
        }
      />
    </>
  )
  return (
    <div className="flex flex-col gap-10 pt-4 pb-10 nav:gap-14 nav:pt-8">
      <h1 className="sr-only">{family.name}</h1>
      <HomeAdd family={family} me={me} />
      {actionSlot && createPortal(controls, actionSlot)}
      <Rings
        family={family}
        spaceId={space?.id}
        me={me}
        people={people}
        narrow={narrow}
        onPerson={(id) => toggle({ by: id })}
      />
      {fresh.error && (
        <div className={page}>
          <FormError error={fresh.error} />
        </div>
      )}
      {fresh.data && (
        <section className={cn(page, '-mt-4 flex flex-col gap-4 nav:-mt-6')} aria-labelledby="home-new">
          <h2 id="home-new" className="sr-only">
            New
          </h2>
          {narrow && 'by' in narrow && (
            <p className="-mt-1 text-sub text-muted-foreground">
              Only {firstName(people.find((x) => x.id === narrow.by)?.name ?? '')}’s.{' '}
              <button
                type="button"
                onClick={() => setNarrow(null)}
                className={cn('font-semibold text-foreground underline-offset-4 hover:underline', link)}
              >
                Show everyone’s
              </button>
            </p>
          )}
          {shown.length > 0 &&
            (view === 'feed' ? (
              <Feed items={shown} />
            ) : view === 'mixed' ? (
              <Wall items={shown} />
            ) : (
              <div className="flex flex-col gap-8">
                {kindsIn(shown).map((kind) => (
                  <section key={kind} aria-label={KIND_LABELS[kind]} className="flex flex-col gap-3">
                    <h3 className="text-lg font-semibold tracking-tight">{KIND_LABELS[kind]}</h3>
                    <Wall items={shown.filter((item) => kindOf(item) === kind)} />
                  </section>
                ))}
              </div>
            ))}
          <CaughtUp
            family={family}
            since={since}
            empty={items.length === 0}
            more={fresh.data.more}
            me={me}
            everything={!caughtUp}
          />
        </section>
      )}
      {fresh.data && <WanderShelves family={family} spaceId={space?.id} />}
    </div>
  )
}

// On this day first, when there's anything from today's date in other years, then whoever has added something new,
// most recent first. A person's ring narrows the wall to theirs; tapping it again shows everyone's.
function Rings({
  family,
  spaceId,
  me,
  people,
  narrow,
  onPerson,
}: {
  family: Family
  spaceId?: string
  me: Me
  people: NewItem['by'][]
  narrow: Narrow
  onPerson: (id: string) => void
}) {
  const sections = mySections(me, family)
  const [day] = useState(localDay)
  const on = useOnThisDay(family.id, spaceId, day, sections.photos || sections.moments)
  const years = on.data ? onThisDayShown(on.data, sections) : []
  const first = years[0]
  const picture = first?.photos.shown[0]
    ? albumPhotoUrl(first.photos.shown[0].ref, 'thumb')
    : first?.moments.shown.find((x) => x.photo)?.photo
      ? momentPhotoUrl(first.moments.shown.find((x) => x.photo)!.photo!, 'thumb')
      : null
  if (!years.length && !people.length) return null
  const ring = 'grid size-17 place-items-center rounded-full p-[2.5px] sm:size-19'
  const label = 'w-19 truncate text-center text-xs text-muted-foreground sm:w-21'
  return (
    <nav aria-label="On this day and who’s added things" className={page}>
      <ul className="-mx-1 flex gap-3 overflow-x-auto px-1 py-1 sm:gap-4">
        {years.length > 0 && (
          <li className="shrink-0">
            <Link
              to={`/circle/${family.id}/on-this-day/story?day=${day}`}
              className={cn('flex flex-col items-center gap-1.5 rounded-2xl', link)}
            >
              <span className={cn(ring, 'fresh-ring')}>
                <span className="grid size-full place-items-center overflow-hidden rounded-full border-[3px] border-background bg-secondary">
                  {picture ? (
                    <img src={picture} alt="" decoding="async" className="size-full object-cover" />
                  ) : (
                    <span className="text-xs font-semibold">{first?.year}</span>
                  )}
                </span>
              </span>
              <span className={cn(label, 'font-semibold text-foreground')}>On this day</span>
            </Link>
          </li>
        )}
        {people.map((person, i) => {
          const picked = !!narrow && 'by' in narrow && narrow.by === person.id
          return (
            <li key={person.id} className="shrink-0">
              <button
                type="button"
                aria-pressed={picked}
                aria-label={`Only what ${firstName(person.name)} added`}
                onClick={() => onPerson(person.id)}
                className={cn('flex flex-col items-center gap-1.5 rounded-2xl', link)}
              >
                <span className={cn(ring, picked ? 'bg-brand' : 'fresh-ring')}>
                  <Initial
                    name={person.name}
                    portrait={person.portrait}
                    index={i + 1}
                    className="size-full border-[3px] border-background text-lg"
                  />
                </span>
                <span className={cn(label, picked && 'font-semibold text-foreground')}>{firstName(person.name)}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

// Everything on one wall, newest first, each thing keeping its own shape.
function Wall({ items }: { items: NewItem[] }) {
  return (
    <ul className="home-wall" aria-label="New things" data-view="grid">
      {items.map((item) => (
        <li key={item.kind === 'photos' ? `photos-${item.photos[0]?.id}` : `${item.kind}-${item.id}`}>
          {item.kind === 'photos' ? (
            <PhotosTile item={item} />
          ) : item.kind === 'recipe' ? (
            <RecipeTile item={item} />
          ) : (
            <MomentTile item={item} />
          )}
        </li>
      ))}
    </ul>
  )
}

// Feed is a picture with the next one showing beneath it, as a feed scrolls (Dallas, 2026-10-18: the single upright
// rectangle didn't say feed).
const VIEWS: Record<Arrange, { label: string; icon: typeof LayoutGrid }> = {
  feed: { label: 'Feed', icon: GalleryVerticalEnd },
  mixed: { label: 'Tiles', icon: LayoutGrid },
  sections: { label: 'By section', icon: Rows3 },
}
const viewButton =
  'grid size-11 shrink-0 place-items-center rounded-full text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:opacity-60 android:size-12 nav:size-10 nav:transition-colors nav:hover:bg-secondary nav:active:opacity-100'

// One button for the view, showing the one you're on; it opens the others (Dallas, 2026-10-18), so it sits with the
// "+" and Filter as one small group of buttons.
function HomeViews({
  views,
  value,
  onChange,
}: {
  views: Arrange[]
  value: Arrange
  onChange: (view: Arrange) => void
}) {
  const { label, icon: Icon } = VIEWS[value]
  return (
    <ActionMenu
      title="View"
      trigger={
        <button type="button" aria-label={`View: ${label}`} title={`View: ${label}`} className={viewButton}>
          <Icon className="size-5.5 stroke-[1.75]" aria-hidden />
        </button>
      }
      groups={[
        views.map((view) => ({
          label: VIEWS[view].label,
          icon: VIEWS[view].icon,
          current: view === value,
          onSelect: () => onChange(view),
        })),
      ]}
      align="end"
    />
  )
}

// One post after another, as Instagram's feed sets them out: whose it is at the top (their face and name, where it
// was added, when), the picture beneath as wide as a phone's screen (a column on bigger screens), then its words, with
// a line between one person's post and the next (Dallas, 2026-10-18). Their face or name opens their profile. Several
// photos from one sitting swipe sideways. No like, count or comment row: appreciation isn't a tally here.
function Feed({ items }: { items: NewItem[] }) {
  const people = useMyPeople()
  const [profile, setProfile] = useState<ShownPerson | null>(null)
  const known = fromMyPeople(people.data ?? [], 'name')[0]?.people ?? []
  const openPerson = useOpenPerson(setProfile)
  const open = (by: Who) => {
    const person = known.find((p) => p.userId === by.id)
    return person ? () => openPerson(person) : undefined
  }
  return (
    <>
      <ul
        className="-mx-5 flex flex-col divide-y divide-border sm:mx-auto sm:w-full sm:max-w-xl"
        aria-label="New things"
        data-view="feed"
      >
        {items.map((item, i) => (
          <li
            key={item.kind === 'photos' ? `photos-${item.photos[0]?.id}` : `${item.kind}-${item.id}`}
            className="py-4 first:pt-0 sm:py-6"
          >
            {item.kind === 'photos' ? (
              <PhotosPost item={item} first={i === 0} onPerson={open(item.by)} />
            ) : item.kind === 'recipe' ? (
              <RecipePost item={item} first={i === 0} onPerson={open(item.by)} />
            ) : (
              <MomentPost item={item} first={i === 0} onPerson={open(item.by)} />
            )}
          </li>
        ))}
      </ul>
      <PersonProfile person={profile} onClose={() => setProfile(null)} />
    </>
  )
}

const post = cn('flex flex-col gap-3 rounded-[1.25rem]', link)
const feedSizes = '(width >= 40rem) 36rem, 100vw'
const article = 'flex flex-col gap-3'

// The top of a post: who posted it, then where it went (the album or book) and when. Tapping them opens their profile,
// or yours.
function PostHead({ by, at, where, onPerson }: { by: Who; at: number; where: string; onPerson?: () => void }) {
  const inside = (
    <>
      <Initial name={by.name} portrait={by.portrait} className="size-10 text-sm" />
      <span className="flex min-w-0 flex-col text-left leading-tight">
        <span className="flex min-w-0 items-baseline gap-1.5 text-sub">
          <span className="truncate font-semibold">{by.name}</span>
          <span aria-hidden="true" className="text-muted-foreground">
            ·
          </span>
          <span className="shrink-0 text-muted-foreground">{when(at)}</span>
        </span>
        <span className="truncate text-xs text-muted-foreground">{where}</span>
      </span>
    </>
  )
  const who = cn('-m-1 flex min-w-0 items-center gap-3 rounded-2xl p-1', link)
  return (
    <header className="flex items-center px-4 sm:px-0">
      {by.you ? (
        <Link to="/me" className={who}>
          {inside}
        </Link>
      ) : onPerson ? (
        <button type="button" onClick={onPerson} className={who}>
          {inside}
        </button>
      ) : (
        <span className="flex min-w-0 items-center gap-3">{inside}</span>
      )}
    </header>
  )
}

type PostProps<T> = { item: T; first: boolean; onPerson?: () => void }

function PhotosPost({ item, first, onPerson }: PostProps<PhotosItem>) {
  const [at, setAt] = useState(0)
  const lead = item.photos[0]
  if (!lead) return null
  const to = item.album ? `/album/${item.album.id}` : `/photo/${lead.id}`
  const ratio = feedRatio(lead.width, lead.height)
  // Past the photos sent with the post, a last slide says how many more there are; the post opens the album.
  const more = item.count - item.photos.length
  const slides = item.photos.length + (more > 0 ? 1 : 0)
  return (
    <article className={article} aria-label={`${item.by.name}’s photos`}>
      <PostHead by={item.by} at={item.at} where={item.album?.title ?? 'Photos'} onPerson={onPerson} />
      <Link to={to} className={post} aria-label={`${photosLine(item)}, ${when(item.at)}`}>
        <span className="relative block">
          <span
            className="flex snap-x snap-mandatory [scrollbar-width:none] overflow-x-auto overscroll-x-contain bg-secondary sm:rounded-[0.75rem] [&::-webkit-scrollbar]:hidden"
            onScroll={(e) => setAt(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}
          >
            {item.photos.map((photo, i) => (
              <img
                key={photo.id}
                src={albumPhotoUrl(photo.ref, 'medium')}
                srcSet={photoSrcSet(photo, ['thumb', 'medium', 'display'])}
                sizes={feedSizes}
                alt=""
                loading={first && i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                style={{ aspectRatio: ratio }}
                className="w-full shrink-0 snap-center object-cover"
              />
            ))}
            {more > 0 && (
              <span
                style={{ aspectRatio: ratio }}
                className="grid w-full shrink-0 snap-center place-items-center text-lg font-semibold"
              >
                {more === 1 ? '1 more photo' : `${more} more photos`}
              </span>
            )}
          </span>
          {item.count > 1 && (
            <span
              aria-hidden="true"
              className="absolute top-3 right-3 rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium text-white tabular-nums"
            >
              {Math.min(at + 1, item.photos.length)}/{item.count}
            </span>
          )}
        </span>
        {slides > 1 && (
          <span aria-hidden="true" className="flex items-center justify-center gap-1">
            {photoDots(slides, at).map(({ i, small }) => (
              <span
                key={i}
                className={cn(
                  'rounded-full transition-colors',
                  small ? 'size-1' : 'size-1.5',
                  i === at ? 'bg-brand' : 'bg-border',
                )}
              />
            ))}
          </span>
        )}
      </Link>
    </article>
  )
}

function RecipePost({ item, first, onPerson }: PostProps<RecipeItem>) {
  return (
    <article className={article} aria-label={`${item.by.name}’s recipe`}>
      <PostHead by={item.by} at={item.at} where={item.book ? item.book.title : 'Recipe'} onPerson={onPerson} />
      <Link to={`/recipe/${item.id}`} className={post}>
        {item.cover ? (
          <img
            src={photoUrl(item.cover, 'display')}
            alt=""
            loading={first ? 'eager' : 'lazy'}
            decoding="async"
            className="aspect-[4/5] w-full bg-secondary object-cover sm:rounded-[0.75rem]"
          />
        ) : (
          <span
            className={cn(
              'mx-4 grid aspect-[4/3] place-items-center rounded-[1.25rem] p-6 text-center text-2xl font-semibold text-balance shadow-paper sm:mx-0',
              tint(2),
            )}
          >
            {item.title}
          </span>
        )}
        <span className="flex flex-col gap-1 px-4 sm:px-0">
          {item.cover && <span className="text-lg leading-snug font-semibold text-balance">{item.title}</span>}
          <span className="text-xs tracking-[0.08em] text-muted-foreground uppercase">
            Recipe{item.book ? ` · ${item.book.title}` : ''}
          </span>
        </span>
      </Link>
    </article>
  )
}

function MomentPost({ item, first, onPerson }: PostProps<MomentItem>) {
  return (
    <article className={article} aria-label={`${item.by.name}’s moment`}>
      <PostHead by={item.by} at={item.at} where="Moment" onPerson={onPerson} />
      <Link to={`/moment/${item.id}`} className={post}>
        {item.photo ? (
          <>
            <img
              src={momentPhotoUrl(item.photo.id, 'medium')}
              srcSet={photoSrcSet({ ...item.photo, ref: item.photo.id }, ['medium', 'display'], momentPhotoUrl)}
              sizes={feedSizes}
              alt=""
              loading={first ? 'eager' : 'lazy'}
              decoding="async"
              style={{ aspectRatio: feedRatio(item.photo.width, item.photo.height) }}
              className="w-full bg-secondary object-cover sm:rounded-[0.75rem]"
            />
            <MomentTitle title={item.title} feed className="px-4 text-lg sm:px-0" />
          </>
        ) : (
          <span className="mx-4 flex flex-col overflow-hidden rounded-[1.25rem] bg-card shadow-paper sm:mx-0">
            <span aria-hidden="true" className="words-bar h-1.5" />
            <span className="flex flex-col gap-2 p-5">
              <MomentTitle title={item.title} feed className="text-xl" />
              <span className="line-clamp-8 text-pretty text-muted-foreground">{item.words}</span>
            </span>
          </span>
        )}
      </Link>
    </article>
  )
}

// A moment's title. On tiles a quiet campfire says it's a moment, not just a picture; the feed's header already says
// Moment, so there a small "Read moment" pill says the story opens. Under a photo the title is all that shows.
function MomentTitle({ title, feed, className }: { title: string; feed?: boolean; className?: string }) {
  return (
    <span className={cn('flex items-start gap-1.5 leading-snug font-semibold', className)}>
      {!feed && <MomentsIcon className="mt-[0.2em] size-[1.05em] shrink-0 text-muted-foreground" />}
      <span className="min-w-0 flex-1 text-balance">{title}</span>
      {feed && (
        <span className="mt-0.5 ml-2 flex shrink-0 items-center gap-0.5 rounded-full border border-border py-1 pr-2 pl-3 text-xs font-medium whitespace-nowrap text-muted-foreground">
          Read moment
          <ChevronRight aria-hidden="true" className="size-3.5" />
        </span>
      )}
    </span>
  )
}

const when = (at: number) => dayLabel(localDay(new Date(at)))
const byLine = (by: NewItem['by']) => (by.you ? 'You' : firstName(by.name))

function Byline({ by, children }: { by: NewItem['by']; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 px-1 text-sub text-muted-foreground">
      <Initial name={by.name} portrait={by.portrait} className="size-6 text-[0.625rem]" />
      <span className="min-w-0 truncate">{children}</span>
    </span>
  )
}

const tile = cn(
  'group flex flex-col gap-2 rounded-[1.25rem] transition-transform duration-300 ease-settle hover:-translate-y-0.5',
  link,
)

// A few photos one person added to one album on one day: the first whole, the next three small beneath it. With more
// than one, the tile swipes sideways through them all without opening the album, as an album's cover does on Photos
// (AlbumPeek): the collage first, then each photo, filling the collage's shape. It scrolls natively with snap points,
// so an upward or downward swipe stays with the page, and a tap anywhere still opens the album.
function PhotosTile({ item }: { item: PhotosItem }) {
  const [at, setAt] = useState(0)
  const [lead, ...rest] = item.photos.slice(0, 4)
  if (!lead) return null
  const to = item.album ? `/album/${item.album.id}` : `/photo/${lead.id}`
  const sizes = '(width >= 80rem) 20vw, (width >= 64rem) 25vw, (width >= 48rem) 33vw, 50vw'
  const collage = (
    <span className="flex w-full shrink-0 snap-center flex-col gap-0.5">
      <img
        src={albumPhotoUrl(lead.ref, 'medium')}
        srcSet={photoSrcSet(lead, ['thumb', 'medium', 'display'])}
        sizes={sizes}
        alt=""
        loading="lazy"
        decoding="async"
        style={{ aspectRatio: `${lead.width} / ${lead.height}` }}
        className="w-full bg-secondary object-cover"
      />
      {rest.length > 0 && (
        <span className="grid grid-cols-3 gap-0.5">
          {rest.map((photo) => (
            <img
              key={photo.id}
              src={albumPhotoUrl(photo.ref, 'thumb')}
              alt=""
              loading="lazy"
              decoding="async"
              className="aspect-square w-full object-cover"
            />
          ))}
        </span>
      )}
    </span>
  )
  const many = item.photos.length > 1
  const slides = item.photos.length + 1
  return (
    <Link to={to} className={tile} aria-label={`${photosLine(item)}, ${when(item.at)}`}>
      <span className="flex flex-col gap-2">
        <span className="block overflow-hidden rounded-[1.25rem] bg-secondary shadow-paper">
          {many ? (
            <span
              data-testid="tile-peek"
              onScroll={(e) => {
                const el = e.currentTarget
                setAt(Math.round(el.scrollLeft / Math.max(el.clientWidth, 1)))
              }}
              className="flex snap-x snap-mandatory [scrollbar-width:none] overflow-x-auto overscroll-x-contain [&::-webkit-scrollbar]:hidden"
            >
              {collage}
              {item.photos.map((photo) => (
                <span key={photo.id} aria-hidden="true" className="relative w-full shrink-0 snap-center">
                  <img
                    src={albumPhotoUrl(photo.ref, 'medium')}
                    srcSet={photoSrcSet(photo, ['thumb', 'medium', 'display'])}
                    sizes={sizes}
                    alt=""
                    // Fetched as the tile nears the screen, not as it's swiped to, so the slides don't arrive blank.
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 size-full object-cover"
                  />
                </span>
              ))}
            </span>
          ) : (
            collage
          )}
        </span>
        {many && (
          <span aria-hidden="true" data-testid="tile-peek-dots" className="flex items-center justify-center gap-1">
            {photoDots(slides, at).map(({ i, small }) => (
              <span
                key={i}
                data-current={i === at || undefined}
                className={cn(
                  'rounded-full bg-foreground/20 transition-colors data-current:bg-foreground/70',
                  small ? 'size-1' : 'size-1.5',
                )}
              />
            ))}
          </span>
        )}
      </span>
      <Byline by={item.by}>
        {item.album ? (
          <>
            {byLine(item.by)} added to <span className="font-semibold text-foreground">{item.album.title}</span>
          </>
        ) : (
          `${byLine(item.by)} added photos`
        )}{' '}
        · {when(item.at)}
      </Byline>
    </Link>
  )
}

function RecipeTile({ item }: { item: RecipeItem }) {
  return (
    <Link to={`/recipe/${item.id}`} className={tile}>
      <span className={cn('block overflow-hidden rounded-[1.25rem] shadow-paper', tint(2))}>
        {item.cover ? (
          <img
            src={photoUrl(item.cover, 'display')}
            alt=""
            loading="lazy"
            decoding="async"
            className="aspect-[4/5] w-full object-cover"
          />
        ) : (
          <span className="grid aspect-[4/3] place-items-center p-5 text-center text-lg font-semibold text-balance">
            {item.title}
          </span>
        )}
      </span>
      <span className="flex flex-col gap-1 px-1">
        {item.cover && <span className="leading-snug font-semibold text-balance">{item.title}</span>}
        <span className="text-xs tracking-[0.08em] text-muted-foreground uppercase">
          Recipe{item.book ? ` · ${item.book.title}` : ''}
        </span>
      </span>
      <Byline by={item.by}>
        {byLine(item.by)} · {when(item.at)}
      </Byline>
    </Link>
  )
}

// A moment with a photo leads with it; one told only in words is a card of its words.
function MomentTile({ item }: { item: MomentItem }) {
  return (
    <Link to={`/moment/${item.id}`} className={tile}>
      {item.photo ? (
        <span className="block overflow-hidden rounded-[1.25rem] bg-secondary shadow-paper">
          <img
            src={momentPhotoUrl(item.photo.id, 'medium')}
            alt=""
            loading="lazy"
            decoding="async"
            style={{ aspectRatio: `${item.photo.width} / ${item.photo.height}` }}
            className="w-full object-cover"
          />
        </span>
      ) : (
        <span className="flex flex-col overflow-hidden rounded-[1.25rem] bg-card shadow-paper">
          <span aria-hidden="true" className="words-bar h-1.5" />
          <span className="flex flex-col gap-2 p-4">
            <MomentTitle title={item.title} className="text-lg" />
            <span className="line-clamp-6 text-sub text-pretty text-muted-foreground">{item.words}</span>
          </span>
        </span>
      )}
      {item.photo && <MomentTitle title={item.title} className="px-1" />}
      <Byline by={item.by}>
        {item.by.you ? 'Your moment' : `${firstName(item.by.name)}’s moment`} · {when(item.at)}
      </Byline>
    </Link>
  )
}

// The end of the feed: with Caught up on, everything since you last looked is above; off, the newest of everything.
// A section with more than Home shows says so.
function CaughtUp({
  family,
  since,
  empty,
  more,
  me,
  everything,
}: {
  family: Family
  since: string
  empty: boolean
  more: Record<Kind, boolean>
  me: Me
  everything: boolean
}) {
  const sections = mySections(me, family)
  const fuller = (Object.keys(SECTION_PATHS) as Kind[]).filter((kind) => more[kind] && sections[kind])
  return (
    <div className="flex flex-col items-center gap-2 border-t border-border/70 pt-8 text-center">
      <span aria-hidden="true" className="fresh-ring size-3 rounded-full" />
      <p className="text-lg font-semibold tracking-tight">
        {everything
          ? empty
            ? 'Nothing here yet'
            : fuller.length > 0
              ? 'That’s the latest'
              : 'That’s everything'
          : empty
            ? 'Nothing new yet'
            : 'You’re caught up'}
      </p>
      <p className="text-sub text-pretty text-muted-foreground">
        {everything
          ? empty
            ? 'No one has added anything yet. Wander below.'
            : fuller.length > 0
              ? 'Older things are in each section.'
              : 'You’ve reached the beginning.'
          : empty
            ? `No one has added anything ${since === 'today' ? 'today' : since}. Wander below.`
            : `That’s everything new ${since}.`}
      </p>
      {fuller.length > 0 && (
        <p className="flex flex-wrap justify-center gap-x-4 text-sub">
          {fuller.map((kind) => (
            <Link
              key={kind}
              to={`/circle/${family.id}/${SECTION_PATHS[kind]}`}
              className={cn(
                'inline-flex min-h-11 items-center gap-1 rounded-lg font-semibold underline-offset-4 hover:underline',
                link,
              )}
            >
              More in {KIND_LABELS[kind]}
              <ChevronRight className="size-4" aria-hidden />
            </Link>
          ))}
        </p>
      )}
    </div>
  )
}

// A few shelves to explore, the day's pick: one person through the circle, one album, one recipe book.
function WanderShelves({ family, spaceId }: { family: Family; spaceId?: string }) {
  const wander = useWander(family.id, spaceId)
  const shelves = wander.data?.shelves ?? []
  if (!shelves.length) return null
  return (
    <section className={cn(page, 'flex flex-col gap-8')} aria-labelledby="home-wander">
      <div className="flex flex-col gap-1">
        <h2 id="home-wander" className="text-title">
          Wander
        </h2>
        <p className="text-sub text-muted-foreground">Something different each day.</p>
      </div>
      {shelves.map((shelf) => (
        <ShelfRow key={shelfKey(shelf)} shelf={shelf} />
      ))}
    </section>
  )
}

const shelfKey = (shelf: Shelf) =>
  shelf.kind === 'person' ? shelf.person.id : shelf.kind === 'album' ? shelf.album.id : shelf.book.id

function ShelfRow({ shelf }: { shelf: Shelf }) {
  const [title, to, eyebrow] =
    shelf.kind === 'person'
      ? [
          shelf.person.living ? `With ${firstName(shelf.person.name)}` : `Remembering ${firstName(shelf.person.name)}`,
          `/person/${shelf.person.id}`,
          'Someone',
        ]
      : shelf.kind === 'album'
        ? [shelf.album.title, `/album/${shelf.album.id}`, 'An album']
        : [shelf.book.title, `/book/${shelf.book.id}`, 'A recipe book']
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <Link to={to} className={cn('group flex items-center gap-3 self-start rounded-xl pr-2', link)}>
        {shelf.kind === 'person' && (
          <Initial name={shelf.person.name} portrait={shelf.person.portrait} className="size-10" />
        )}
        <span className="flex flex-col">
          <span className="text-xs tracking-[0.08em] text-muted-foreground uppercase">{eyebrow}</span>
          <span className="text-lg font-semibold tracking-tight group-hover:underline group-hover:underline-offset-4">
            {title}
          </span>
        </span>
        <ChevronRight className="size-5 text-muted-foreground" aria-hidden />
      </Link>
      <ul className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
        {shelf.items.map((item) => (
          <li key={`${item.kind}-${item.id}`} className="w-40 shrink-0 sm:w-48">
            <ShelfTile item={item} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function ShelfTile({ item }: { item: ShelfItem }) {
  const frame = 'block aspect-[4/5] w-full overflow-hidden rounded-2xl bg-secondary shadow-paper'
  const caption = 'mt-2 line-clamp-2 px-0.5 text-sub leading-snug'
  if (item.kind === 'photo') return <ShelfPhotoTile photo={item} frame={frame} caption={caption} />
  if (item.kind === 'recipe')
    return (
      <Link to={`/recipe/${item.id}`} className={cn('block rounded-2xl', link)}>
        <span className={cn(frame, tint(2))}>
          {item.cover ? (
            <img
              src={photoUrl(item.cover, 'thumb')}
              alt=""
              loading="lazy"
              decoding="async"
              className="size-full object-cover"
            />
          ) : (
            <span className="grid size-full place-items-center p-3 text-center font-semibold text-balance">
              {item.title}
            </span>
          )}
        </span>
        <span className={cn(caption, 'font-semibold')}>{item.title}</span>
      </Link>
    )
  return (
    <Link to={`/moment/${item.id}`} className={cn('block rounded-2xl', link)}>
      <span className={cn(frame, !item.photo && 'flex flex-col bg-card')}>
        {item.photo ? (
          <img
            src={momentPhotoUrl(item.photo.id, 'thumb')}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ) : (
          <>
            <span aria-hidden="true" className="words-bar h-1.5 shrink-0" />
            <span className="line-clamp-8 p-3 text-sub text-pretty text-muted-foreground">{item.words}</span>
          </>
        )}
      </span>
      <span className={cn(caption, 'font-semibold')}>{item.title}</span>
    </Link>
  )
}

function ShelfPhotoTile({ photo, frame, caption }: { photo: ShelfPhoto; frame: string; caption: string }) {
  const year = photo.takenOn?.slice(0, 4)
  const open = usePhotoLink()
  return (
    <Link {...open(photo.id)} className={cn('block rounded-2xl', link)}>
      <span className={frame}>
        <img
          src={albumPhotoUrl(photo.ref, 'thumb')}
          alt={photo.caption}
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
        />
      </span>
      {(photo.caption || year) && (
        <span className={cn(caption, 'text-muted-foreground')}>
          {photo.caption || year}
          {photo.caption && year ? ` · ${year}` : ''}
        </span>
      )}
    </Link>
  )
}
